// ============================================================================
// shared/entrees.ts — LES ENTRÉES EN SCÈNE
// ----------------------------------------------------------------------------
// Trois secondes au début d'un niveau : le héros arrive, puis la partie commence.
// C'est la récompense de fin de campagne (décision Claude-Alain, 01/08).
//
// ⚠️ CE CATALOGUE EST VOLONTAIREMENT SÉPARÉ DE `shared/loots.ts`, ET CE N'EST PAS
// UN DÉTAIL DE RANGEMENT. Une entrée en scène NE SE TIRE PAS dans la Mine : elle
// se mérite en finissant une campagne. La mettre dans LOOTS l'aurait fait entrer
// dans le tirage (`pickLoot` puise dans le catalogue entier), dans la pyramide
// des raretés, dans le codex `/pages/loots.html` — et dans le test qui refuse un
// loot absent de cette page. Séparer, c'est garder les deux économies étanches :
// la Mine donne au hasard, la campagne récompense un effort.
//
// Module PUR : ni DOM, ni base, ni dessin. Le dessin vit dans
// client/entree-scene.ts, l'attribution dans server/campaigns.ts.
// ============================================================================

/** Le slot d'équipement. Une seule entrée portée à la fois, comme un chapeau. */
export const SLOT_ENTREE = 'entree'

export interface Entree {
  /** L'identifiant stocké en base (inventaire, équipement, campagne). */
  id: string
  nom: string
  /** Ce que le joueur voit se passer, en une phrase. */
  desc: string
  /**
   * `false` = codée et jouable, mais qu'aucune campagne ne peut encore donner :
   * le vestiaire l'affiche en « Prochainement ». Garder ces entrées visibles est
   * délibéré — une vitrine qui ne montre que ce qu'on possède ne donne envie de
   * rien.
   */
  attribuable: boolean
}

/**
 * LES ENTRÉES. L'ordre est celui de la planche /pages/entrees.html et celui du
 * vestiaire : on ne le change pas sans raison, c'est le repère de Claude-Alain.
 */
export const ENTREES: readonly Entree[] = [
  { id: 'entree-cage', nom: 'La cage de mine', attribuable: true,
    desc: 'Une cage grillagée descend au bout de son câble, la grille se relève, tu sors.' },
  { id: 'entree-percement', nom: 'Le percement', attribuable: true,
    desc: 'Le sol tressaille, les fissures s\'ouvrent, tu perces la brique et tu te hisses.' },
  { id: 'entree-reception', nom: 'La réception', attribuable: true,
    desc: 'Tu tombes du ciel et tu te reçois à trois points, un genou et un poing au sol.' },
  { id: 'entree-wagonnet', nom: 'Le wagonnet', attribuable: true,
    desc: 'Un wagonnet arrive lancé, freine dans les étincelles, tu sautes en marche.' },
  { id: 'entree-rideau', nom: 'Le rideau de papier', attribuable: false,
    desc: 'Deux grandes feuilles s\'écartent comme un rideau, et tu te déplies.' },
  { id: 'entree-lampe', nom: 'La lampe', attribuable: false,
    desc: 'Noir complet, une allumette, puis ta lampe s\'allume et découvre le niveau.' },
]

/**
 * L'entrée jouée AU DÉBUT DE CHAQUE NIVEAU D'UNE CAMPAGNE, pour tout le monde,
 * sans rien avoir gagné (décision Claude-Alain : « pour la 6 on la lance pour
 * les campagnes seulement par défaut »).
 *
 * ⚠️ Elle n'est donc ni possédée, ni équipable, ni attribuable en récompense :
 * elle appartient au mode campagne, pas au joueur. Le vestiaire l'affiche quand
 * même, avec cette explication — sans quoi un joueur qui la voit en jeu la
 * chercherait en vain dans ses affaires.
 */
export const ENTREE_CAMPAGNE = 'entree-lampe'

/** L'entrée que l'auteur peut mettre en jeu comme récompense de sa campagne. */
export const ENTREES_ATTRIBUABLES: readonly Entree[] = ENTREES.filter((e) => e.attribuable)

export function entreeById(id: string): Entree | undefined {
  return ENTREES.find((e) => e.id === id)
}

/** Vrai si `id` peut être choisi comme récompense d'une campagne. La route de
 *  création s'appuie dessus : le client ne décide de rien. */
export function estAttribuable(id: unknown): id is string {
  return typeof id === 'string' && (entreeById(id)?.attribuable ?? false)
}

/** Vrai si `id` peut être ÉQUIPÉ (donc joué hors campagne). L'entrée de campagne
 *  ne l'est pas : elle se joue toute seule, là où elle a un sens. */
export function estEquipable(id: unknown): id is string {
  return typeof id === 'string' && id !== ENTREE_CAMPAGNE && entreeById(id) !== undefined
}
